import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider } from '@material-ui/core';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import {
  REDIRECT_TO_SESSION_KEY,
  CANOPY_SIGN_UP_CALLBACK,
  CANOPY_LOGIN_CALLBACK,
} from '@/utils/constants';
import sleepioTheme from '@/themes/sleepioTheme';

// Entry point for external services that still redirect to the old
// /activate/sleepio-canopy/:lob/:org_slug/:intent/email/callback format.
// Waits 1500ms for Supabase to settle the session before calling getSession(),
// then routes to /coverage (Create) or /complete (Login) via usePostAuthNavigation.

const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  const fallbackCallback = state.accountIntent === AccountIntent.Signin
    ? CANOPY_LOGIN_CALLBACK
    : CANOPY_SIGN_UP_CALLBACK;
  const effectiveRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY) || fallbackCallback;
  const navigatePostAuth = usePostAuthNavigation(effectiveRedirectTo);

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[EmailCallback]', 'No session after magic link');
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
      }

      navigatePostAuth({ replace: true });
    };

    handleCallback().catch((err) => {
      logError('[EmailCallback]', 'Callback failed', err);
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
